import { CircularProgress, Dialog, IconButton } from "@mui/material";
import type { ComponentType } from "react";

import { type ClientTool } from "@/shared/domain/agentThread/clientTools";
import {
  resolveApprovalMode,
  type ApprovalMode,
  type SavedToolPermissions,
} from "@/shared/domain/agentThread/clientTools/approvalModes";
import { useReleasedClientTools } from "@/shared/domain/agentThread/hooks/useReleasedClientTools";
import { useSavedToolPermissions } from "@/shared/domain/agentThread/hooks/useSavedToolPermissions";
import { useUpdateClientToolPreference } from "@/shared/domain/agentThread/hooks/useUpdateClientToolPreference";
import {
  BookmarkIcon,
  CloseIcon,
  DashboardIcon as BoardIcon,
  ExploreIcon,
  StorageIcon,
} from "@/shared/foundation/icons/mui";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ClientToolSettingsModal.module.css";
import { ToolApprovalModeSelector } from "./ToolApprovalModeSelector";

// ---------------------------------------------------------------------------
// Tool categories
// ---------------------------------------------------------------------------

interface ToolCategory {
  id: string;
  name: string;
  Icon: ComponentType<{ className?: string }>;
  toolNames: string[];
}

const CATEGORIES: ToolCategory[] = [
  {
    id: "nav",
    name: "Navigation & Views",
    Icon: ExploreIcon,
    toolNames: [
      "client_navigate",
      "client_visualize_open_panel",
      "client_visualize_add_to_panel",
      "client_visualize_remove_from_panel",
      "client_visualize_remove_panel",
      "client_visualize_search_topic",
      "client_visualize_open_file",
      "client_visualize_seek",
      "client_visualize_apply_layout",
      "client_visualize_set_log_panel_filter",
    ],
  },
  {
    id: "views",
    name: "Saved views",
    Icon: BookmarkIcon,
    toolNames: ["client_apply_view"],
  },
  {
    id: "boards",
    name: "Boards",
    Icon: BoardIcon,
    toolNames: [
      "client_create_board",
      "client_dry_run_board",
      "client_update_board",
      "client_read_board",
      "client_read_panel_draft",
      "client_update_panel_draft",
      "client_publish_board_clause",
    ],
  },
  {
    id: "data",
    name: "Data & Events",
    Icon: StorageIcon,
    toolNames: ["client_create_events", "client_create_session_events"],
  },
];

/**
 * Resolve category → tool modules from `modules`. Tools not listed
 * in any category are appended to the last category.
 */
function getCategorizedTools(modules: readonly ClientTool[]): {
  category: ToolCategory;
  tools: ClientTool[];
}[] {
  const categorizedNames = new Set(CATEGORIES.flatMap((c) => c.toolNames));

  const uncategorized = modules.filter(
    (t) => !categorizedNames.has(t.spec.name),
  );

  const result = CATEGORIES.map((category) => ({
    category,
    tools: category.toolNames
      .map((name) => modules.find((t) => t.spec.name === name))
      .filter((t): t is ClientTool => t !== undefined),
  })).filter((entry) => entry.tools.length > 0);

  // Append any uncategorized tools to the last category
  if (uncategorized.length > 0 && result.length > 0) {
    result[result.length - 1].tools.push(...uncategorized);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ClientToolSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal for configuring per-tool approval modes.
 *
 * Tools are grouped into categories. Each tool has a 3-way segmented
 * control for selecting its approval mode. Changes are persisted
 * immediately via optimistic update.
 *
 * The modal offers a change only once the saved permissions are loaded. A
 * change to one tool writes the whole record, built from the loaded one;
 * before the record loads, that write would replace every saved choice with
 * the one entry. Reset to defaults deletes every saved choice, so it too waits
 * until the person can see what is saved.
 */
export function ClientToolSettingsModal({
  open,
  onClose,
}: ClientToolSettingsModalProps) {
  const { permissions, reload } = useSavedToolPermissions();
  const { updatePreference, resetPreferences } =
    useUpdateClientToolPreference();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Tool Permissions</h2>
          <p className={styles.subtitle}>
            Control how AI tools execute actions in your workspace.
          </p>
        </div>
        <IconButton
          aria-label="Close tool settings"
          onClick={onClose}
          size="small"
          className={styles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Body */}
      <div className={styles.body}>
        <SavedPermissionsBody
          permissions={permissions}
          onReload={reload}
          onChange={updatePreference}
        />
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.resetButton}
          onClick={resetPreferences}
          disabled={permissions.status !== "loaded"}
        >
          Reset to defaults
        </button>
        <Button variant="contained" onClick={onClose} size="small">
          Done
        </Button>
      </div>
    </Dialog>
  );
}

interface SavedPermissionsBodyProps {
  permissions: SavedToolPermissions;
  onReload: () => void;
  onChange: (toolName: string, mode: ApprovalMode) => void;
}

/**
 * The modal's body for each state of the saved permissions: a spinner while
 * they load, the tools and their modes once loaded, and otherwise a way to
 * request them again.
 */
function SavedPermissionsBody({
  permissions,
  onReload,
  onChange,
}: SavedPermissionsBodyProps) {
  const { modules } = useReleasedClientTools();

  if (permissions.status === "loading") {
    return (
      <div className={styles.loading}>
        <CircularProgress size="1.5rem" />
      </div>
    );
  }

  if (permissions.status === "unavailable") {
    return (
      <div className={styles.unavailable}>
        <p className={styles.unavailableText}>
          Your saved tool permissions could not be loaded. Until they load,
          every tool asks before it runs and you can&apos;t change permissions
          here.
        </p>
        <Button variant="outlined" size="small" onClick={onReload}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <>
      {getCategorizedTools(modules).map(({ category, tools }) => (
        <div key={category.id} className={styles.category}>
          <div className={styles.categoryHeader}>
            <category.Icon className={styles.categoryIcon} />
            {category.name}
          </div>

          <div className={styles.toolList}>
            {tools.map((tool) => (
              <div key={tool.spec.name} className={styles.toolRow}>
                <div className={styles.toolInfo}>
                  <span className={styles.toolName}>{tool.displayName}</span>
                  <span className={styles.toolDescription}>
                    {tool.displayDescription}
                  </span>
                </div>
                <ToolApprovalModeSelector
                  selectedMode={resolveApprovalMode(
                    tool,
                    permissions.preferences,
                  )}
                  allowableModes={tool.allowableApprovalModes}
                  onChange={(mode) => onChange(tool.spec.name, mode)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
